import React from 'react';
import { Asset } from 'contentful';

import type { AffiliateHero2Props, greenlightBrandingEnum } from '../index';

import { getImageData } from '~/utils/contentful';

import { Box } from '../../../Box';
import { Column } from '../../../Layout';
import CtaWidget from '../../../CtaWidget';
import { AffiliateLogoGrouping } from '../AffiliateLogoGrouping';
import PrivacyPolicyDisclaimer from '../../../PrivacyPolicyDisclaimer';
import cx from 'classnames';

import { DesktopImage, QrCodeWrapper, QrCodeContainer } from '../styles';

interface PropsWithOverride extends AffiliateHero2Props {
  qrContainerId?: string;
}

export default function DesktopView({
  headingText,
  useLightHeadingText,
  subheadingText,
  useLightSubheadingText,
  fullwidthDesktopImage,
  desktopCta,
  affiliateLogo,
  useGreenlightIcon,
  greenlightBranding,
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  sendConsentEvent,
  useEmbeddedSmartLink,
  qrContainerId
}: PropsWithOverride) {
  const visibility = fullwidthDesktopImage ? 'visible' : 'hidden';

  return (
    <section
      className="desktop-block-container my-12 px-15 py-8 tablet:mt-3 tablet:flex"
      data-blockid="affiliate-hero-20-hero-desktop"
      data-e2e="hero"
    >
      <div className="layout layout-padding mt-2 !flex-nowrap gap-8">
        <div className="column flex-col items-center justify-center tablet:w-1/2">
          <div className="flex max-w-[532px] flex-col">
            {affiliateLogo && (
              <AffiliateLogoGrouping
                affiliateLogo={affiliateLogo}
                useGreenlightIcon={useGreenlightIcon}
                greenlightBranding={
                  greenlightBranding as greenlightBrandingEnum
                }
              />
            )}
            <h1
              className={cx(
                'mb-4 mt-4 text-left text-4xl desktop:text-5xl',
                useLightHeadingText ? 'text-white' : 'text-black'
              )}
              data-testid="affiliate-hero-20-headline-desktop"
            >
              {headingText}
            </h1>
            <p
              className={cx(
                'mb-6 mt-0 text-left text-xl leading-6',
                useLightSubheadingText ? 'text-white' : 'text-black'
              )}
              data-testid="affiliate-hero-20-subhead-desktop"
            >
              {subheadingText}
            </p>
            {desktopCta && (
              <Box data-testid="affiliate-hero-desktop-cta">
                <CtaWidget
                  {...desktopCta?.fields}
                  id="affiliate-hero-desktop-cta-widget"
                  sysId={desktopCta?.sys?.contentType?.sys?.id}
                  module="Hero"
                  sendConsentEvent={sendConsentEvent}
                />
              </Box>
            )}

            {privacyPolicyDisclaimer && (
              <Box marginTop="ml">
                <PrivacyPolicyDisclaimer
                  privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                  displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                />
              </Box>
            )}
          </div>
        </div>
        <Column size={{ tablet: 7, desktop: 6 }}>
          <Box className="relative" flex="1">
            {useEmbeddedSmartLink ? (
              <QrCodeWrapper>
                <QrCodeContainer>
                  <div id={qrContainerId} data-testid={qrContainerId} />
                </QrCodeContainer>
              </QrCodeWrapper>
            ) : (
              <DesktopImage
                {...getImageData(fullwidthDesktopImage as Asset)}
                visibility={visibility}
                heroImage={true}
                loading="eager"
              />
            )}
          </Box>
        </Column>
      </div>
    </section>
  );
}
